import { useCallback, useEffect, useRef, useState } from 'react';

/** Selection events arriving this soon after the pane opened are the seed (the cursor as it was), not a gesture. */
export const SEED_WINDOW_MS = 1500;

export const selectionKey = (sel: { sheet: string; address: string } | null): string | null => (sel ? `${sel.sheet}!${sel.address}` : null);

/**
 * Whether the selection travels with the next messages (product owner, 2026-09-10): OFF when the pane opens and on
 * every new / opened thread — the cursor as it was is not a request — and ON as soon as the user selects a cell or
 * a range afterwards; the chip's × turns it off again until the next selection gesture.
 */
export function useSelectionOptIn(selection: { sheet: string; address: string } | null): [boolean, (included: boolean) => void, () => void] {
  const [included, setIncluded] = useState(false);
  const mountedAt = useRef(Date.now());
  const lastKey = useRef<string | null>(selectionKey(selection));

  useEffect(() => {
    const key = selectionKey(selection);
    if (key === lastKey.current) return;
    lastKey.current = key;
    if (key && Date.now() - mountedAt.current > SEED_WINDOW_MS) setIncluded(true);
  }, [selection]);

  /** New / opened thread: back to off; the next selection gesture adds it again. */
  const reset = useCallback(() => setIncluded(false), []);
  return [included, setIncluded, reset];
}
